import styles from './mailing-lists.module.css'

import { LuDownload } from '@react-icons/all-files/lu/LuDownload'
import { LuEllipsis } from '@react-icons/all-files/lu/LuEllipsis'
import { LuFileUp } from '@react-icons/all-files/lu/LuFileUp'
import { LuFrown } from '@react-icons/all-files/lu/LuFrown'
import { LuTrash2 } from '@react-icons/all-files/lu/LuTrash2'
import { LuUserPlus } from '@react-icons/all-files/lu/LuUserPlus'
import { LuUsers } from '@react-icons/all-files/lu/LuUsers'
import type {
  ColumnDef,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table'
import orderBy from 'lodash/orderBy'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, Outlet, useNavigate } from 'react-router-dom'

import {
  exportEmailGroup,
  loadMailingListsNextPage,
} from '@disco/state-management/actions/EmailGroupActions'
import { getUser } from '@disco/state-management/selectors/user'
import dayjs from '@disco/utils/dayjs'

import type { MailingList, RecipientListId } from 'types/list'

import EmptyMessage from 'components/common/empty-message'
import DocumentHeadTags from 'components/document-head-tags'
import {
  Box,
  Button,
  DataTable,
  Flex,
  Heading,
  Link,
  SearchTextField,
  Spinner,
  Text,
} from 'components/ds'
import type { RowAction } from 'components/ds/DataTable'
import DownloadModal from 'components/modals/download-modal'

import { useDispatch, useSelector } from 'hooks/use-redux'

import {
  CAPTION_EXISTING,
  CAPTION_EXPORT,
  CAPTION_IMPORT,
  CAPTION_SINGLE,
} from '../add/buttons'
import IconEmptyLists from './icon-empty-lists'
import MailingListsSelectionMenu from './selection-action-menu'

const DEFAULT_SORT = { id: 'created', desc: true }

const SORT_ITERATEES: Record<string, (_list: MailingList) => unknown> = {
  name: list => list.name?.toLowerCase(),
  count_recipients: list => list.count_recipients ?? 0,
  created: list => list.created,
}

const HighlightMatch = ({
  text,
  query,
}: {
  text: string
  query: string
}): React.ReactNode => {
  if (!query) return text
  const index = text.toLowerCase().indexOf(query.toLowerCase())
  if (index < 0) return text
  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.highlight}>
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  )
}

const ContactsMailingLists = (): null | React.ReactNode => {
  const emailGroups = useSelector(state => state.emailGroups)
  const isAdmin = useSelector(getUser)?.is_admin
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [modalOpen, setModalOpen] = useState(false)
  const [downloadUrl, setDownloadUrl] = useState(null)
  const [deferred, setDeferred] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [exportId, setExportId] = useState<RecipientListId | null>(null)
  const [sorting, setSorting] = useState<SortingState>([DEFAULT_SORT])
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [searchTerm, setSearchTerm] = useState('')
  const searchQuery = searchTerm.trim()

  const exportClick = useCallback(
    async (id: RecipientListId) => {
      setLoading(true)
      setError(false)
      setDeferred(false)
      setDownloadUrl(null)
      setModalOpen(true)
      const result: Record<string, any> = await dispatch(exportEmailGroup(id))

      if (result?.deferred) {
        setDeferred(true)
        setLoading(false)
      }

      if (result?.download_url) {
        setDownloadUrl(result?.download_url)
        setLoading(false)
      }

      if (result?.error) {
        setError(true)
        setLoading(false)
      }
    },
    [dispatch],
  )

  const closeModal = () => setModalOpen(false)

  const fetchNextPage = useCallback(() => {
    dispatch(loadMailingListsNextPage())
  }, [dispatch])

  const renderModal = () => {
    if (!modalOpen) return null

    if (downloadUrl) {
      return (
        <DownloadModal type="base" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (loading) {
      return (
        <DownloadModal type="loading" onClose={closeModal} url={downloadUrl} />
      )
    }

    if (error && exportId) {
      return (
        <DownloadModal
          type="error"
          onClose={closeModal}
          onClick={() => exportClick(exportId)}
        />
      )
    }

    if (deferred) {
      return <DownloadModal type="deferred" onClose={closeModal} />
    }
  }

  const columns = useMemo<ColumnDef<MailingList>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        enableResizing: false,
        cell: ({ row }) => (
          <Link asChild color="gray" highContrast underline="hover">
            <RouterLink to={`/lists/${row.original.id}`}>
              <span translate="no">
                <HighlightMatch text={row.original.name} query={searchQuery} />
              </span>
            </RouterLink>
          </Link>
        ),
      },
      {
        accessorKey: 'count_recipients',
        header: 'Contacts',
        enableResizing: false,
        cell: ({ row }) => (
          <Link asChild color="gray" highContrast underline="hover">
            <RouterLink to={`/lists/${row.original.id}`}>
              {row.original.count_recipients ?? 0}
            </RouterLink>
          </Link>
        ),
      },
      {
        accessorKey: 'created',
        header: 'Created',
        enableResizing: false,
        cell: ({ row }) => (
          <span
            title={dayjs(row.original.created).format('MMMM Do YYYY, h:mma')}
          >
            {dayjs(row.original.created).format('MMMM Do YYYY')}
          </span>
        ),
      },
    ],
    [searchQuery],
  )

  const getRowActions = useCallback(
    (list: MailingList): RowAction[] => [
      {
        label: 'Actions',
        icon: <LuEllipsis size={16} />,
        subActions: [
          { heading: 'Add contacts' },
          {
            label: CAPTION_EXISTING,
            icon: <LuUsers size={14} />,
            onClick: () => navigate(`/contacts/list?append=${list.id}`),
          },
          {
            label: CAPTION_IMPORT,
            icon: <LuFileUp size={14} />,
            onClick: () => navigate(`/lists/${list.id}/file-import`),
          },
          {
            label: CAPTION_SINGLE,
            icon: <LuUserPlus size={14} />,
            onClick: () => navigate(`/lists/${list.id}/add-single`),
          },
          {}, // separator
          {
            label: CAPTION_EXPORT,
            icon: <LuDownload size={14} />,
            disabled: !isAdmin,
            tooltip: isAdmin ? undefined : 'Only admins can export contacts.',
            onClick: () => {
              setExportId(list.id)
              exportClick(list.id)
            },
          },
          {}, // separator
          {
            label: 'Delete',
            icon: <LuTrash2 size={14} />,
            color: 'red',
            onClick: () => navigate(`/lists/delete-list/${list.id}`),
          },
        ],
      },
    ],
    [exportClick, isAdmin, navigate],
  )

  const selectedLists = useMemo(
    () => emailGroups.mailingList.filter(list => rowSelection[list.id]),
    [emailGroups.mailingList, rowSelection],
  )

  const mailingLists = useMemo(() => {
    const query = searchQuery.toLowerCase()
    const filtered = query
      ? emailGroups.mailingList.filter(list =>
          list.name?.toLowerCase().includes(query),
        )
      : emailGroups.mailingList
    // Un-sorting (third header click) falls back to the default order
    const sort = sorting[0] ?? DEFAULT_SORT
    const iteratee = SORT_ITERATEES[sort.id] ?? SORT_ITERATEES.created
    return orderBy(filtered, iteratee, sort.desc ? 'desc' : 'asc')
  }, [emailGroups.mailingList, sorting, searchQuery])

  // The initial-load window before the fetch dispatches (isFetchingMailingLists
  // and mailingListsFetched both start false) should render the same skeleton
  // as the fetch itself, not a blank page.
  const isLoading =
    emailGroups.isFetchingMailingLists || !emailGroups.mailingListsFetched
  const createNewListButton = (
    <Button size="3" onClick={() => navigate('/lists/create')}>
      Create new list
    </Button>
  )
  const totalCount =
    emailGroups.mailingListTotalCount ?? emailGroups.mailingList.length
  const allLoaded = emailGroups.mailingList.length >= totalCount
  // Search only filters what's loaded so far. Without this, a match sitting
  // on an unloaded page would show "no results" forever - the empty
  // filtered set means DataTable's own scroll-triggered fetch never gets a
  // sentinel row to observe. Keep pulling pages until a match turns up or
  // there's nothing left to load.
  const searchingUnloadedPages =
    !isLoading && !!searchQuery && mailingLists.length === 0 && !allLoaded
  useEffect(() => {
    if (searchingUnloadedPages && !emailGroups.isFetchingNextMailingListsPage) {
      fetchNextPage()
    }
  }, [
    searchingUnloadedPages,
    emailGroups.isFetchingNextMailingListsPage,
    fetchNextPage,
  ])
  let content

  if (isLoading || emailGroups.mailingList.length > 0) {
    content = (
      <div className={styles.table}>
        <DataTable
          data={mailingLists}
          columns={columns}
          loading={isLoading}
          getRowActions={getRowActions}
          alwaysShowRowActions
          size="2"
          sorting={sorting}
          onSortingChange={setSorting}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          hasNextPage={!isLoading && !allLoaded}
          isFetchingNextPage={emailGroups.isFetchingNextMailingListsPage}
          onFetchNextPage={fetchNextPage}
          hasNextPageError={emailGroups.mailingListNextPageError}
          onRetryNextPage={fetchNextPage}
          emptyState={
            searchingUnloadedPages ? (
              <Flex direction="column" align="center" gap="2" py="8">
                <Spinner size="3" />
                <Text size="2" color="gray">
                  Searching the rest of your lists…
                </Text>
              </Flex>
            ) : (
              <Flex direction="column" align="center" gap="2" py="8">
                <LuFrown size={28} />
                <Text weight="medium">No lists match your search</Text>
                <Text size="2" color="gray">
                  Nothing here for “{searchQuery}”. Try a shorter word or a
                  different spelling.
                </Text>
                <Button
                  variant="ghost"
                  color="gray"
                  onClick={() => setSearchTerm('')}
                >
                  Clear search
                </Button>
              </Flex>
            )
          }
          topRowContent={
            <Flex align="center" gap="3">
              <Box width="280px">
                <SearchTextField
                  placeholder="Search lists"
                  value={searchTerm}
                  onChange={evt => setSearchTerm(evt.target.value)}
                  disabled={isLoading}
                />
              </Box>
              {!isLoading && (
                <Text size="2" color="gray">
                  {searchQuery ? (
                    <>
                      <Text weight="medium">{mailingLists.length}</Text> of{' '}
                      <Text weight="medium">
                        {emailGroups.mailingList.length}
                      </Text>{' '}
                      loaded lists
                    </>
                  ) : allLoaded ? (
                    <>
                      <Text weight="medium">{totalCount}</Text> lists
                    </>
                  ) : (
                    <>
                      <Text weight="medium">
                        {emailGroups.mailingList.length}
                      </Text>{' '}
                      of <Text weight="medium">{totalCount}</Text> lists
                    </>
                  )}
                </Text>
              )}
            </Flex>
          }
        />
        {!isLoading && selectedLists.length >= 2 && (
          <MailingListsSelectionMenu
            selectedLists={selectedLists}
            onDeleted={deletedIds => {
              // Only clear the ones that actually deleted - a partial
              // failure leaves the rest selected so the user can retry
              setRowSelection(prev => {
                const next = { ...prev }
                deletedIds.forEach(id => delete next[id])
                return next
              })
            }}
          />
        )}
      </div>
    )
  } else {
    content = (
      <div className={styles.emptyMessage}>
        <EmptyMessage
          icon={<IconEmptyLists />}
          header="No mailing lists yet"
          cta={
            <div>
              Create your first mailing list by clicking the button below.
              <div className={styles.emptyButtonContainer}>
                {createNewListButton}
              </div>
            </div>
          }
        />
      </div>
    )
  }

  return (
    <>
      <DocumentHeadTags title="Mailing Lists | DISCO" />
      <Flex align="center" justify="between" mb="5">
        {/* my="0" beats the browser's default h1 margin, which this app's
            global CSS doesn't reset */}
        <Heading size="7" my="0">
          Mailing Lists
        </Heading>
        {createNewListButton}
      </Flex>
      {content}
      <Outlet />
      {renderModal()}
    </>
  )
}

export default ContactsMailingLists
